import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  base_url: string = environment.api.url;

  constructor(private http: HttpClient, private authService: AuthService, private router: Router) {}

  post(service: string, params: any, success: ((res: any) => void)|null = null, fail: ((res: ErrorResponse) => void)|null = null): void {
    const headers: any = {};

    if (this.authService.isLoggedIn() && environment.web) {
      headers['Authorization'] = 'Bearer ' + this.authService.token;
    }

    if (!params) {
      params = {};
    }

    params.__url__ = location.toString();
    params.source = 'web';

    this.http.post(this.base_url + '/' + service, params, {
      headers: new HttpHeaders(headers)
    }).subscribe(
      (res: any): void => this.handleResponse(res, success, fail),
      (error: HttpErrorResponse): void => this.handleResponse(error, success, fail)
    );
  }

  handleResponse(res: SuccessResponse|HttpErrorResponse, success: ((res: any) => void)|null = null, fail: ((res: ErrorResponse) => void)|null = null): void {
    if (res instanceof HttpErrorResponse) {
      if (res.error) {
        res = res.error;
      }
    }

    if ((res as SuccessResponse).success) {
      if (success) success(res);
    }
    else {
      if (fail) fail(res as ErrorResponse);

      if ((res as ErrorResponse).error === 401) {
        this.authService.logout();
        this.router.navigate(['/connexion']);
      }
    }
  }

  convertToFormData(data: any, formData: FormData, parentKey: string|null): FormData {
    if (data === null || data === undefined) return null;

    formData = formData || new FormData();

    if (typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
      Object.keys(data).forEach((key: string): void => {
        this.convertToFormData(data[key], formData, (!parentKey ? key : (data[key] instanceof File ? parentKey : `${parentKey}[${key}]`)));
      });
    }
    else {
      formData.append(parentKey!, data);
    }

    return formData;
  }

  upload(service: string, params: any, files: any, progress: ((progress: number) => void)|null = null, success: ((res: UploadResponse) => void)|null = null, fail: ((res: ErrorResponse) => void)|null = null): void {
    const formData: FormData = new FormData();
    const headers: any = {};

    formData.set('source', 'web');

    this.convertToFormData(params, formData, null);

    if (files instanceof FileList) {
      for (let i: number = 0; i < files.length; i++) {
        formData.append('files[]', files.item(i)!, files.item(i)!.name);
      }
    }
    else if (files instanceof File) {
      formData.append('files[]', files, files.name);
    }
    else if (files instanceof Blob) {
      formData.append('files[]', files, params['file']);
    }

    if (this.authService.isLoggedIn() && environment.web) {
      headers['Authorization'] = 'Bearer ' + this.authService.token;
    }

    let url: string = this.base_url + '/' + service;

    if (service.startsWith('http')) {
      url = service;
    }

    this.http.request(new HttpRequest('POST', url, formData, {
      reportProgress: true,
      headers: new HttpHeaders(headers)
    })).subscribe((event: HttpEvent<any>): void => {
      switch (event.type) {
        case HttpEventType.Sent: {
          if (progress) progress(0);

          break;
        }

        case HttpEventType.UploadProgress: {
          if (progress) progress(Math.round(100 * event.loaded / event.total!));

          break;
        }

        case HttpEventType.Response: {
          const res: any = event.body;

          if (res.success) {
            if (success) success(res);
          }
          else {
            if (fail) fail(res as ErrorResponse);
          }

          break;
        }
      }
    }, (err: HttpErrorResponse): void => {
      if (!(err.error instanceof Error)) {
        const res: any = err.error;

        if (res.success) {
          if (success) success(res);
        }
        else {
          if (fail) fail(res as ErrorResponse);
        }
      }
    });
  }

  logout(callback: () => void): void {
    this.post('logout', {}, (_: any): void => {
      this.authService.logout();
      callback();
    }, (_: ErrorResponse): void => {
      this.authService.logout();
      callback();
    });
  }

}

export interface SuccessResponse {
  success: boolean;
  data?: any;
}

export interface MsDocumentUpload {
  id: string;
}

export interface UploadResponse {
  documents: {
    success: boolean;
    document: MsDocumentUpload
  }[];
}

export interface ErrorResponse extends SuccessResponse {
  error: number;
  message: string|null;
}

export interface LoginResponse extends SuccessResponse {
  data: {
    token: string;
    lang: 'fr'|'en';
  };
}

export interface DocumentUploadResponse extends SuccessResponse {
  data: any;
}

export interface DocumentListResponse extends SuccessResponse {
  data: any[];
}

export interface DocumentPagination {
  id: string;
  created: string;
  updated: string;
  filename: string;
  document_type: string;
  signed: boolean;
  nb_signature_require: number;
  signature_date: string;
  can_delete: boolean;
  hidden: boolean;
}

export interface DocumentPaginationResponse extends SuccessResponse {
  success: boolean;
  data: {
    count: number;
    pages: number;
    list: DocumentPagination[];
  }
}


export interface TotpResponse extends SuccessResponse {
  data: {
    url: string;
    secret: string;
  };
}

export interface ApiKeys {
  id: string;
  created_at: string;
  name: string;
  client_id: string;
  client_secret: string;
}

export interface ApiKeysListResponse extends SuccessResponse {
  data: ApiKeys[];
}

export interface ApiKeysResponse extends SuccessResponse {
  data: ApiKeys;
}

export interface User {
  name: string
  firstname: string|null;
  email: string;
  phone: string;
  siret: string|null;
  phone_valid: boolean;
  email_unsigned_document: boolean;
  has_totp: boolean;
  has_api_access: boolean;
  token_source: string;
  photo: any;
  credit: number;
  is_moral: boolean;
}

export interface UserResponse extends SuccessResponse {
  data: User;
}

export interface RegistrationGetResponse extends SuccessResponse {
  data: {
    is_moral: boolean;
    name: string;
    firstname: string|null;
    email: string;
    phone: string;
    siret: string|null;
  };
}

export interface Invoice {
  invoice_id: string;
  price: number;
  paid_date: string;
  credit: number;
  stripe_receipt_url: string|null;
  has_invoice_pdf: boolean;
}

export interface InvoiceListResponse extends SuccessResponse {
  data: Invoice[];
}

export interface BackendSigner {
  id: string;
  email: string;
  name: string;
  firstname: string
  phone: string;
  signature_require: boolean;
}

export interface Signer extends BackendSigner {
  selected: boolean;
  display: boolean;
}

export interface SignerListResponse extends SuccessResponse {
  data: BackendSigner[];
}

export interface SignaturesLinkResponse extends SuccessResponse {
  data: {
    default: string|null;
    custom: string|null;
    photo: string|null;
  };
}

export interface DocumentPdfResponse extends SuccessResponse {
  data: {
    document: any;
    users: any[];
    current_user: any;
  }
}
